import type { DataTableSort } from '../components/primitives';

/**
 * Sorts fully-loaded rows by one top-level field, the way every portal table
 * does it: numbers numerically, everything else as case-insensitive text,
 * with empty values last whichever way the column points.
 *
 * Rows are sorted on the field named by `sort.key`, so nested values must be
 * flattened onto the row before sorting.
 *
 * @param rows - The rows.
 * @param sort - The active sort, or undefined for the rows as given.
 * @returns A new, sorted array.
 */
export function sortRows<T>(rows: readonly T[], sort: DataTableSort | undefined): T[] {
  if (sort === undefined) return [...rows];
  const factor = sort.direction === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const left = (a as Record<string, unknown>)[sort.key];
    const right = (b as Record<string, unknown>)[sort.key];
    const leftEmpty = left === null || left === undefined || left === '';
    const rightEmpty = right === null || right === undefined || right === '';
    if (leftEmpty) return rightEmpty ? 0 : 1;
    if (rightEmpty) return -1;
    if (typeof left === 'number' && typeof right === 'number') return (left - right) * factor;
    return String(left).localeCompare(String(right), undefined, { sensitivity: 'base' }) * factor;
  });
}
